// Pure shaping helper for the exhibition QR board (#4). Flattens the three
// data sources — exhibition, zones, and exhibits (all camelCase per ADR-0020)
// — into uniform QrItems grouped by zone, so the page component stays a thin
// render over hooks. Kept separate + pure so the grouping is unit-testable
// without mocking React Query.

export interface QrItem {
  id: string;
  kind: "exhibition" | "zone" | "exhibit";
  kindLabel: string;
  title: string;
  code: string;
  qrCodeUrl: string | null;
  coverImageId: string | null;
}

export interface QrZoneGroup {
  zone: QrItem;
  exhibits: QrItem[];
}

export interface QrBoard {
  exhibition: QrItem | null;
  zones: QrZoneGroup[];
}

interface ExhibitionInput {
  id: string;
  titleTc: string;
  code: string;
  qrCodeUrl?: string | null;
  coverImageId?: string | null;
}

interface ZoneInput {
  id: string;
  titleTc: string;
  code: string;
  qrCodeUrl?: string | null;
  coverImageId?: string | null;
  exhibitIds: string[];
}

interface ExhibitInput {
  id: string;
  titleTc: string;
  code: string;
  qrCodeUrl?: string | null;
  coverImageId?: string | null;
}

export function buildQrBoard(
  exhibition: ExhibitionInput | undefined | null,
  zones: ZoneInput[],
  exhibits: ExhibitInput[],
): QrBoard {
  const exhibitById = new Map(exhibits.map((e) => [e.id, e]));

  return {
    exhibition: exhibition
      ? {
          id: exhibition.id,
          kind: "exhibition",
          kindLabel: "Exhibition",
          title: exhibition.titleTc,
          code: exhibition.code,
          qrCodeUrl: exhibition.qrCodeUrl ?? null,
          coverImageId: exhibition.coverImageId ?? null,
        }
      : null,
    zones: zones.map((z) => ({
      zone: {
        id: z.id,
        kind: "zone",
        kindLabel: "Zone",
        title: z.titleTc,
        code: z.code,
        qrCodeUrl: z.qrCodeUrl ?? null,
        coverImageId: z.coverImageId ?? null,
      },
      exhibits: z.exhibitIds
        .map((eid) => exhibitById.get(eid))
        .filter((e): e is ExhibitInput => Boolean(e))
        .map((e) => ({
          id: e.id,
          kind: "exhibit" as const,
          kindLabel: "Exhibit",
          title: e.titleTc,
          code: e.code,
          qrCodeUrl: e.qrCodeUrl ?? null,
          coverImageId: e.coverImageId ?? null,
        })),
    })),
  };
}
